import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useSimulationStore } from '@/state/simulationStore';
import { useBrainStore } from '@/state/brainStore';
import Phase1Tab from '@/screens/reporting/Phase1Tab';
import OverviewTab from '@/screens/reporting/OverviewTab';
import TargetsTab from '@/screens/reporting/TargetsTab';
import LeadsTab from '@/screens/reporting/LeadsTab';
import CallsTab from '@/screens/reporting/CallsTab';
import RebooksTab from '@/screens/reporting/RebooksTab';
import MoneyTab from '@/screens/reporting/MoneyTab';
import CustomersTab from '@/screens/reporting/CustomersTab';
import LeversTab from '@/screens/reporting/LeversTab';
import ReportDateFilter from '@/components/reporting/ReportDateFilter';
import ReportingTabBoundary from '@/components/reporting/ReportingTabBoundary';
import { daysInRange, dayKeyOf, targetWeek } from '@/domain/reportingEngine';
import { useActiveStudio } from '@/state/useActiveStudio';
import { useSessionStore } from '@/state/sessionStore';
import { isAllStudios, ALL_STUDIOS_SCOPE, type ActiveStudio } from '@/domain/studioScope';
import type { StudioId } from '@/domain/types';

const TABS = [
  { key: 'phase1', label: 'Phase 1' },
  { key: 'overview', label: 'Overview' },
  { key: 'targets', label: 'Targets' },
  { key: 'leads', label: 'Leads & marketing' },
  { key: 'calls', label: 'Calls & agents' },
  { key: 'rebooks', label: 'Rebooks & artists' },
  { key: 'money', label: 'Money' },
  { key: 'customers', label: 'Customers' },
  { key: 'levers', label: 'Levers' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const TAB_KEYS = new Set<string>(TABS.map((t) => t.key));

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export default function ReportingDashboard() {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const studios = useBrainStore((s) => s.studios);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab') ?? 'overview';
  const tab: TabKey = (TAB_KEYS.has(rawTab) ? rawTab : 'overview') as TabKey;

  const activeStudios = useMemo(() => Object.values(studios).filter((s) => !s.archived), [studios]);
  const studioIds = useMemo(() => new Set(activeStudios.map((s) => s.id)), [activeStudios]);

  // Date range: ?from=&to= day-keys, default the current target week (Thu-Tue) so
  // every tab opens on the same operational week the Targets tab uses.
  const todayKey = dayKeyOf(clockIso);
  const defaultWeek = useMemo(() => targetWeek(todayKey).days, [todayKey]);
  const rawFrom = searchParams.get('from');
  const rawTo = searchParams.get('to');
  const from = rawFrom && ISO_DAY.test(rawFrom) ? rawFrom : defaultWeek[0]!;
  const to = rawTo && ISO_DAY.test(rawTo) ? rawTo : defaultWeek[defaultWeek.length - 1]!;
  const days = useMemo(() => daysInRange(from, to), [from, to]);

  // Studio scope now follows the top-bar studio switcher (single source of truth)
  // instead of a local ?studio= param. 'all' maps to the network-wide scope.
  const { activeStudio, assignedStudios, isMultiStudio } = useActiveStudio();
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);
  const studioId: StudioId | undefined =
    !isAllStudios(activeStudio) && studioIds.has(activeStudio as StudioId)
      ? (activeStudio as StudioId)
      : undefined;
  const studioSelectValue = studioId ?? ALL_STUDIOS_SCOPE;
  // Only offer studios this persona is assigned to (the switcher's own gate), so
  // the in-page filter can never scope the app beyond the persona's remit.
  const selectableStudios = useMemo(
    () => activeStudios.filter((s) => assignedStudios.includes(s.id)),
    [activeStudios, assignedStudios],
  );

  const patchParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  return (
    <section className="px-8 py-8" data-testid="reporting-dashboard">
      <header className="max-w-6xl">
        <p className="label-small-caps text-gold-bright">PRD §12.15</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Reporting Dashboard</h1>
        <p className="font-serif text-cream-soft mt-3">
          Live funnel from Meta ad spend to booked appointment. Per-studio, per-agent, per-campaign.
        </p>
      </header>

      {/* Tab bar (deep-linkable via ?tab=) + date-range and studio filters (consistent across tabs) */}
      <div className="mt-6 flex items-end justify-between max-w-6xl flex-wrap gap-3">
        <nav className="flex flex-wrap gap-1" role="tablist" data-testid="reporting-tabs">
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => patchParams({ tab: t.key })}
                data-testid={`reporting-tab-${t.key}`}
                className={clsx(
                  'px-3 py-1.5 text-xs font-inter uppercase tracking-widest rounded-md transition border',
                  active
                    ? 'bg-gold/15 text-gold-bright border-gold-dim/40'
                    : 'text-cream-soft border-transparent hover:text-gold-bright',
                )}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={studioSelectValue}
            onChange={(e) => setActiveStudio(e.target.value as ActiveStudio)}
            data-testid="reporting-studio-filter"
            aria-label="Studio filter"
            className="rounded-md border border-gold-dim/30 bg-ink-soft/40 px-3 py-1.5 text-xs font-inter text-cream-soft hover:border-gold-dim/60 transition"
          >
            {isMultiStudio ? <option value={ALL_STUDIOS_SCOPE}>All studios</option> : null}
            {selectableStudios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <ReportDateFilter
            from={from}
            to={to}
            todayIso={clockIso}
            onChange={(f, t) => patchParams({ from: f, to: t })}
          />
        </div>
      </div>

      <div className="mt-6">
        <ReportingTabBoundary key={`${tab}|${from}|${to}|${studioId ?? 'all'}`} label={TABS.find((t) => t.key === tab)?.label ?? 'report'}>
          {tab === 'phase1' ? <Phase1Tab days={days} studioId={studioId} /> : null}
          {tab === 'overview' ? <OverviewTab days={days} studioId={studioId} /> : null}
          {tab === 'targets' ? <TargetsTab /> : null}
          {tab === 'leads' ? <LeadsTab days={days} studioId={studioId} /> : null}
          {tab === 'calls' ? <CallsTab days={days} studioId={studioId} /> : null}
          {tab === 'rebooks' ? <RebooksTab days={days} studioId={studioId} /> : null}
          {tab === 'money' ? <MoneyTab days={days} studioId={studioId} /> : null}
          {tab === 'customers' ? <CustomersTab days={days} studioId={studioId} /> : null}
          {tab === 'levers' ? <LeversTab days={days} /> : null}
        </ReportingTabBoundary>
      </div>
    </section>
  );
}
